// prechartingService.js
import { authApiService } from './authService';

export const prechartingService = {
  createOrUpdatePrechartingData: async (sessionId, prechartingData) => {
    try {
      const response = await authApiService.post(`/precharting/${sessionId}`, prechartingData);
    
      if (response.data?.statusCode === 200) {
        return {
          success: true,
          data: response.data.body?.data || response.data.body,
          message: response.data.body?.message || 'Precharting data saved successfully'
        };
      }
      
      return {
        success: true,
        data: response.data?.data || response.data,
        message: 'Precharting data saved successfully'
      };
    } catch (error) {
      console.error('Error creating/updating precharting data:', error);
      return {
        success: false,
        message: error.response?.data?.body?.message || error.response?.data?.message || 'Failed to save precharting data',
        error: error.response?.data || error.message
      };
    }
  },

  getPrechartingData: async (sessionId) => {
    try {
      const response = await authApiService.get(`/precharting/${sessionId}`);
      
      if (response.data?.statusCode === 200) {
        return {
          success: true,
          data: response.data.body?.data || response.data.body,
          message: response.data.body?.message || 'Precharting data retrieved successfully'
        };
      }
      
      return {
        success: true,
        data: response.data?.data || response.data,
        message: 'Precharting data retrieved successfully'
      };
    } catch (error) {
      console.error('Error fetching precharting data:', error);
      return {
        success: false,
        message: error.response?.data?.body?.message || error.response?.data?.message || 'Failed to fetch precharting data',
        error: error.response?.data || error.message
      };
    }
  },

  updatePrechartingSection: async (sessionId, section, sectionData) => {
    try {
      const response = await authApiService.put(`/precharting/${sessionId}/section/${section}`, sectionData);
      
      if (response.data?.statusCode === 200) {
        return {
          success: true,
          data: response.data.body?.data || response.data.body,
          message: response.data.body?.message || `${section} section updated successfully`
        };
      }
      
      return {
        success: true,
        data: response.data?.data || response.data,
        message: `${section} section updated successfully`
      };
    } catch (error) {
      console.error(`Error updating ${section} section:`, error);
      return {
        success: false,
        message: error.response?.data?.body?.message || error.response?.data?.message || `Failed to update ${section} section`,
        error: error.response?.data || error.message
      };
    }
  },

  completePrechartingData: async (sessionId) => {
    try {
      const response = await authApiService.patch(`/precharting/${sessionId}/complete`);
      
      if (response.data?.statusCode === 200) {
        return {
          success: true,
          message: response.data.body?.message || 'Precharting data marked as completed'
        };
      }
      
      return {
        success: true,
        message: 'Precharting data marked as completed'
      };
    } catch (error) {
      console.error('Error completing precharting data:', error);
      return {
        success: false,
        message: error.response?.data?.body?.message || error.response?.data?.message || 'Failed to complete precharting data',
        error: error.response?.data || error.message
      };
    }
  },

  deletePrechartingData: async (sessionId) => {
    try {
      const response = await authApiService.delete(`/precharting/${sessionId}`);
      
      if (response.data?.statusCode === 200) {
        return {
          success: true,
          message: response.data.body?.message || 'Precharting data deleted successfully'
        };
      }
      
      return {
        success: true,
        message: 'Precharting data deleted successfully'
      };
    } catch (error) {
      console.error('Error deleting precharting data:', error);
      return {
        success: false,
        message: error.response?.data?.body?.message || error.response?.data?.message || 'Failed to delete precharting data',
        error: error.response?.data || error.message
      };
    }
  },

  /**
   * Get all precharting data for a user with pagination
   * @param {Object} params - Query parameters
   * @param {number} params.page - Page number (default: 1)
   * @param {number} params.limit - Items per page (default: 10)
   * @param {string} params.status - Filter by status (draft, completed, archived)
   * @param {string} params.patientId - Filter by patient ID
   * @returns {Promise<Object>} API response
   */
  getAllPrechartingData: async (params = {}) => {
    try {
      const queryParams = new URLSearchParams();
      
      if (params.page) queryParams.append('page', params.page);
      if (params.limit) queryParams.append('limit', params.limit);
      if (params.status) queryParams.append('status', params.status);
      if (params.patientId) queryParams.append('patientId', params.patientId);
      
      const response = await authApiService.get(`/precharting?${queryParams.toString()}`);
      
      if (response.data?.statusCode === 200) {
        return {
          success: true,
          data: response.data.body?.data || response.data.body,
          pagination: response.data.body?.pagination,
          message: response.data.body?.message || 'Precharting data retrieved successfully'
        };
      }
      
      return {
        success: true,
        data: response.data?.data || response.data,
        pagination: response.data?.pagination,
        message: 'Precharting data retrieved successfully'
      };
    } catch (error) {
      console.error('Error fetching all precharting data:', error);
      return {
        success: false,
        message: error.response?.data?.body?.message || error.response?.data?.message || 'Failed to fetch precharting data',
        error: error.response?.data || error.message
      };
    }
  },

  searchICD10Codes: async (query, limit = 20) => {
    try {
      const response = await authApiService.get(`/precharting/icd10/search?q=${encodeURIComponent(query)}&limit=${limit}`);
      
      if (response.data?.statusCode === 200) {
        return {
          success: true,
          data: response.data.body?.data || response.data.body?.data || [],
          count: response.data.body?.count || response.data.body?.data?.length || 0,
          message: response.data.body?.message || 'Search completed successfully'
        };
      }
      
      return {
        success: true,
        data: response.data?.data || [],
        count: response.data?.data?.length || 0,
        message: 'Search completed successfully'
      };
    } catch (error) {
      console.error('Error searching ICD-10-CM codes:', error);
      return {
        success: false,
        message: error.response?.data?.body?.message || error.response?.data?.message || 'Failed to search ICD-10-CM codes',
        error: error.response?.data || error.message,
        data: []
      };
    }
  },

  getRelatedICD10Codes: async (code, limit = 12) => {
    try {
      const response = await authApiService.get(`/precharting/icd10/related/${encodeURIComponent(code)}?limit=${limit}`);

      if (response.data?.statusCode === 200) {
        return {
          success: true,
          data: response.data.body?.data || [],
          count: response.data.body?.count || response.data.body?.data?.length || 0,
          message: response.data.body?.message || 'Related codes retrieved successfully'
        };
      }

      return {
        success: true,
        data: response.data?.data || [],
        count: response.data?.data?.length || 0,
        message: 'Related codes retrieved successfully'
      };
    } catch (error) {
      console.error('Error fetching related ICD-10-CM codes:', error);
      return {
        success: false,
        message: error.response?.data?.body?.message || error.response?.data?.message || 'Failed to fetch related ICD-10-CM codes',
        error: error.response?.data || error.message,
        data: []
      };
    }
  },

  lookupICD10Code: async (code) => {
    try {
      const response = await authApiService.get(`/precharting/icd10/code/${encodeURIComponent(code)}`);
      
      if (response.data?.statusCode === 200) {
        return {
          success: true,
          data: response.data.body?.data || response.data.body,
          message: response.data.body?.message || 'Code retrieved successfully'
        };
      }
      
      return {
        success: true,
        data: response.data?.data || response.data,
        message: 'Code retrieved successfully'
      };
    } catch (error) {
      console.error('Error looking up ICD-10-CM code:', error);
      return {
        success: false,
        message: error.response?.data?.body?.message || error.response?.data?.message || 'Failed to lookup ICD-10-CM code',
        error: error.response?.data || error.message
      };
    }
  },

  // Medication APIs
  searchMedications: async (query, limit = 20) => {
    try {
      const response = await authApiService.get(`/precharting/medications/search?q=${encodeURIComponent(query)}&limit=${limit}`);
      
      if (response.data?.statusCode === 200) {
        return {
          success: true,
          data: response.data.body?.data || response.data.body?.data || [],
          count: response.data.body?.count || response.data.body?.data?.length || 0,
          message: response.data.body?.message || 'Search completed successfully'
        };
      }
      
      return {
        success: true,
        data: response.data?.data || [],
        count: response.data?.data?.length || 0,
        message: 'Search completed successfully'
      };
    } catch (error) {
      console.error('Error searching medications:', error);
      return {
        success: false,
        message: error.response?.data?.body?.message || error.response?.data?.message || 'Failed to search medications',
        error: error.response?.data || error.message,
        data: []
      };
    }
  },

  lookupMedication: async (medicationName) => {
    try {
      const response = await authApiService.get(`/precharting/medications/lookup/${encodeURIComponent(medicationName)}`);
      
      if (response.data?.statusCode === 200) {
        return {
          success: true,
          data: response.data.body?.data || response.data.body,
          message: response.data.body?.message || 'Medication retrieved successfully'
        };
      }
      
      return {
        success: true,
        data: response.data?.data || response.data,
        message: 'Medication retrieved successfully'
      };
    } catch (error) {
      console.error('Error looking up medication:', error);
      return {
        success: false,
        message: error.response?.data?.body?.message || error.response?.data?.message || 'Failed to lookup medication',
        error: error.response?.data || error.message
      };
    }
  },

  validateMedications: async (medicationNames) => {
    try {
      const response = await authApiService.post(`/precharting/medications/validate`, { medicationNames });
      
      if (response.data?.statusCode === 200) {
        return {
          success: true,
          data: response.data.body?.data || response.data.body,
          message: response.data.body?.message || 'Validation completed successfully'
        };
      }
      
      return {
        success: true,
        data: response.data?.data || response.data,
        message: 'Validation completed successfully'
      };
    } catch (error) {
      console.error('Error validating medications:', error);
      return {
        success: false,
        message: error.response?.data?.body?.message || error.response?.data?.message || 'Failed to validate medications',
        error: error.response?.data || error.message
      };
    }
  }
};
